import { resolveAssetUrl } from "./mediaUrl";

export interface BrandingAssets {
  faviconMediaUrl?: string | null;
  faviconUrl?: string | null;
  logoMediaUrl?: string | null;
  logoUrl?: string | null;
  name?: string | null;
  nameAr?: string | null;
}

/** Keep in sync with `common.loading` in `src/i18n/locales/en.json`. */
export const DEFAULT_DOCUMENT_TITLE_EN = "Loading...";
/** Keep in sync with `common.loading` in `src/i18n/locales/ar.json`. */
export const DEFAULT_DOCUMENT_TITLE_AR = "جاري التحميل...";
const LEGACY_DOCUMENT_TITLES = new Set(["POS System", "نظام نقاط البيع"]);
/**
 * Last-known tenant branding for the index.html bootstrap script.
 * Keep this key in sync with the inline script in `index.html`.
 */
export const DOCUMENT_BRANDING_STORAGE_KEY = "document-branding";

export interface StoredDocumentBranding {
  name: string;
  nameAr: string | null;
  faviconHref: string | null;
}

export function defaultDocumentTitle(language: string): string {
  return language === "ar" ? DEFAULT_DOCUMENT_TITLE_AR : DEFAULT_DOCUMENT_TITLE_EN;
}

export function isDefaultDocumentTitle(title: string): boolean {
  const trimmed = title.trim();
  return (
    !trimmed ||
    trimmed === DEFAULT_DOCUMENT_TITLE_EN ||
    trimmed === DEFAULT_DOCUMENT_TITLE_AR ||
    LEGACY_DOCUMENT_TITLES.has(trimmed)
  );
}

export function resolveBrandingFaviconUrl(
  branding: BrandingAssets | null | undefined,
): string | null {
  if (!branding) return null;
  return resolveAssetUrl(
    branding.faviconMediaUrl ??
      branding.faviconUrl ??
      branding.logoMediaUrl ??
      branding.logoUrl,
    { width: 64, height: 64, quality: 80 },
  );
}

export function resolveBrandingTitle(
  branding: BrandingAssets | null | undefined,
  language: string,
): string | undefined {
  if (!branding) return undefined;
  const localized = (
    language === "ar" ? branding.nameAr : branding.name
  )?.trim();
  return localized || branding.name?.trim() || undefined;
}

export function isSafeFaviconHref(href: string): boolean {
  const trimmed = href.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith("data:image/")) return true;
  if (trimmed.startsWith("//")) return false;
  if (trimmed.startsWith("/")) return true;
  try {
    const url = new URL(trimmed, "https://example.invalid");
    return url.protocol === "https:" || url.protocol === "http:";
  } catch {
    return false;
  }
}

export function shouldReplaceDocumentTitle(
  currentTitle: string,
  nextTitle: string,
  cachedTitle?: string,
): boolean {
  const current = currentTitle.trim();
  if (current === nextTitle.trim()) return false;
  if (isDefaultDocumentTitle(current)) return true;
  return Boolean(cachedTitle && current === cachedTitle);
}

function faviconType(href: string): string {
  const path = href.toLowerCase().split("?")[0];
  if (path.endsWith(".svg") || path.includes("image/svg")) return "image/svg+xml";
  if (path.endsWith(".png") || path.includes("image/png")) return "image/png";
  if (path.endsWith(".ico")) return "image/x-icon";
  if (path.endsWith(".webp")) return "image/webp";
  return "";
}

export function applyDocumentFavicon(href: string): void {
  if (!isSafeFaviconHref(href)) return;
  const link =
    document.querySelector<HTMLLinkElement>("link[rel~='icon']") ??
    document.head.appendChild(document.createElement("link"));
  link.rel = "icon";
  link.type = faviconType(href);
  link.href = href;
}

export function readStoredDocumentBranding(): StoredDocumentBranding | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(DOCUMENT_BRANDING_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredDocumentBranding>;
    const name = typeof parsed.name === "string" ? parsed.name.trim() : "";
    const nameAr =
      typeof parsed.nameAr === "string" && parsed.nameAr.trim()
        ? parsed.nameAr.trim()
        : null;
    const faviconHref =
      typeof parsed.faviconHref === "string" && isSafeFaviconHref(parsed.faviconHref)
        ? parsed.faviconHref.trim()
        : null;
    if (!name && !nameAr && !faviconHref) return null;
    return { name, nameAr, faviconHref };
  } catch {
    return null;
  }
}

export function persistDocumentBranding(
  branding: StoredDocumentBranding,
): void {
  if (typeof localStorage === "undefined") return;
  const previous = readStoredDocumentBranding();
  const name = branding.name.trim() || previous?.name || "";
  const nameAr = branding.nameAr?.trim() || previous?.nameAr || null;
  const faviconHref =
    (branding.faviconHref && isSafeFaviconHref(branding.faviconHref)
      ? branding.faviconHref.trim()
      : null) ||
    previous?.faviconHref ||
    null;
  if (!name && !nameAr && !faviconHref) return;
  try {
    localStorage.setItem(
      DOCUMENT_BRANDING_STORAGE_KEY,
      JSON.stringify({ name, nameAr, faviconHref }),
    );
  } catch {
    /* quota / private mode */
  }
}
